/**
 * 音声チューニングナレッジの静的データ (正源)。
 *
 * ## このファイルの位置づけ
 *
 * Readme を **「構文辞典」ではなく「音声チューニングナレッジベース」** として
 * 育てるための本体。事例 (`useCases.ts`) が「こうすれば直る」の完成形だけを
 * 載せるのに対し、本ファイルは **調べた結果そのもの**を状態付きで残す。
 *
 * 解決できた事例だけを載せると、次の 2 つが失われる。
 *
 *   1. 「調べたが直らなかった」という事実 (同じ調査が繰り返される)
 *   2. 「改善はするが確実ではない」という期待値 (直らないと不信につながる)
 *
 * そのため 1 件ごとに `status` (`solved` / `mitigated` / `unsolved`) と
 * `verifiedAt` (最終確認日) を必須で持たせ、**未解決も残す**。
 *
 * ## 状態の付け方 (定義は `knowledgeStatus.ts` が正源)
 *
 * | 状態        | 付ける条件                                             |
 * | ----------- | ------------------------------------------------------ |
 * | `solved`    | 別の語・別の放送でも同じ手で直った (再現性がある)       |
 * | `mitigated` | 改善する手はあるが確実ではない (語・話者更新で変わる)   |
 * | `unsolved`  | 有効な手段が未特定 / 原因が分かっていない               |
 *
 * ⚠️ 迷ったら **下げる**。「解決済み」が直らないとナレッジ全体が信用されない。
 *
 * ## 追加・編集の手順 (後から編集しやすい構造を優先している)
 *
 *   1. 本配列へ 1 件足す (必須は id / title / status / verifiedAt / problem / nextAction / tags)
 *   2. 分かっている範囲だけ書く (cause / workaround / ssml は任意)
 *   3. 再確認したら `verifiedAt` を更新する (状態が変わらなくても更新する)
 *   4. 対策が固まったら `useCases.ts` の事例へ昇格し、
 *      ナレッジ側は `relatedUseCaseId` を付けて **残す** (経緯を消さない)
 *
 * 画面側の変更は不要 (`UseCasePanel` は props でナレッジを受ける)。
 *
 * ## 将来の YAML 化
 *
 * 本ファイルは「定数配列 + 型注釈」だけで構成し、関数・条件分岐・
 * 他モジュール由来の値を **持たない**。よって配列部分をそのまま
 * YAML へ書き出せる (読み込み側だけを差し替えればよい)。
 */
import type { SsmlTuningKnowledge } from './types';

/**
 * ナレッジ一覧 (配列順が画面の表示順)。
 *
 * 並びは **状態ではなく話題のまとまり** 順にしている
 * (読み → 言い回し → アクセント)。状態で並べ替えると、
 * 関連する項目が離れて「同じ話題の記録が揃っているか」が分からなくなる。
 * 状態は各カードのバッジで判断できる。
 */
export const SSML_TUNING_KNOWLEDGE: readonly SsmlTuningKnowledge[] = [
  {
    id: 'date-reading',
    title: '日付の読み',
    status: 'solved',
    verifiedAt: '2026-09-15',
    problem:
      '「4日」が「よんにち」と読まれるなど、日本語の日付特有の読み (ついたち / ふつか / …) にならない。運休・工事の告知で日付を誤読すると、利用者が別の日と受け取る。',
    cause:
      '数字 + 「日」が単位付きの数値として扱われ、日付としての読み替えが行われないため。文脈からは日付と判断されない。',
    workaround:
      '<say-as interpret-as="date"> で日付として読ませる。format を指定すると「2026-09-15」形式の文字列でも読める。読みを固定したい場合は <sub alias="…"> でカナを直接与える (こちらは確実だが、日付を書き換えるたびに読みも直す必要がある)。',
    ssml: '<speak>工事は<say-as interpret-as="date" format="md">9-4</say-as>に行います。</speak>',
    nextAction:
      '1〜31 日を通しで聴き、say-as だけで全て正しく読めるかを確認する。崩れる日付があれば sub との併用対象として一覧に残す。',
    tags: ['date', 'number'],
  },
  {
    id: 'with-time-to-spare',
    title: '余裕をもって',
    status: 'mitigated',
    verifiedAt: '2026-09-15',
    problem:
      '「余裕をもってご乗車ください」が一息で流れ、注意喚起として耳に残らない。定型句のため聞き流されやすい。',
    cause:
      '定型句は文中で平坦に読まれ、前後の文と抑揚の差が付かないため注意が向かない。語自体の読み間違いではない。',
    workaround:
      '「余裕をもって」の直前に短い間を置き、句の話速を落として前後とコントラストを作る。聞き取りやすさは上がるが「耳に残る」かは主観評価になるため、確実とは言えない。',
    ssml: '<speak>発車時刻が変更となります。<break time="400ms"/><prosody rate="90%">余裕をもって</prosody>ご乗車ください。</speak>',
    nextAction:
      '間 (300〜500ms) と話速 (88〜95%) の組み合わせを現場で聴き比べ、定型句として採用する 1 つの形を決める。決まれば事例へ昇格する。',
    tags: ['phrasing', 'elderly'],
  },
  {
    id: 'some-trains',
    title: '一部の列車に',
    status: 'mitigated',
    verifiedAt: '2026-09-15',
    problem:
      '「一部の列車に遅れが出ています」の「一部の」が弱く聞こえ、全列車が遅れていると誤解される懸念がある。',
    cause:
      '限定を表す語が文頭近くで速く読まれ、後続の「遅れが出ています」に注意が集まるため。範囲を限定する語ほど聞き逃されやすい。',
    workaround:
      '「一部の」を話速低下 + 直前の間で目立たせる。誤解が減るかは利用者の受け取り方に依存するため、効果は聞き取り調査で確認する必要がある。',
    ssml: '<speak>ただ今、<break time="300ms"/><prosody rate="88%">一部の列車に</prosody>遅れが出ています。</speak>',
    nextAction:
      '現場で聞き取り確認を行い、「全部が遅れている」と受け取られないかを検証する。文面変更 (「すべての列車ではありません」の追加) と比較する。',
    tags: ['phrasing', 'apology', 'number'],
  },
  {
    id: 'direction-accent-knowledge',
    title: '方面アクセント',
    status: 'mitigated',
    verifiedAt: '2026-09-15',
    problem:
      '「◯◯方面」の地名側アクセントが平板になったり、「方面」に不自然な高さが付いて地名として認識されない。路線名と紛れる地名 (例: 「なかもず」) で起きやすい。',
    cause:
      '地名 + 接尾語が 1 語として扱われ、地名側のアクセント核が失われるため。ただし ph 指定が無視される語もあり、無視される条件は特定できていない。',
    workaround:
      '<phoneme alphabet="x-amazon-pron-kana"> でアクセント核 (\u0027) 付きのカナ読みを与える。併せて「方面」の直前に弱い間を入れると境界が保たれやすい。⚠️ 確実ではない。ph が無視される語があり、話者・エンジンの更新で結果が変わる。採用は必ず試聴で判断する。',
    ssml: '<speak><phoneme alphabet="x-amazon-pron-kana" ph="ナカ\u0027モズ">なかもず</phoneme><break strength="weak"/>方面ゆき、普通電車です。</speak>',
    nextAction:
      '方面別に採用した ph を一覧化し、ph が無視される語の条件 (語長・拍数・既知語かどうか) を突き合わせて傾向を探る。',
    tags: ['direction', 'accent', 'station-name'],
    relatedUseCaseId: 'direction-accent',
  },
  {
    id: 'station-name-intonation',
    title: '駅名イントネーション',
    status: 'unsolved',
    verifiedAt: '2026-09-15',
    problem:
      '駅名によって語尾が上がる / 平板になるなど抑揚が揺れ、同じ路線の案内でも駅ごとに印象が変わる。特定の駅名だけ不自然に聞こえる。',
    cause:
      '**根本原因が分かっていない。** 同じ拍数・同じ語尾の駅名でも揺れる駅と揺れない駅があり、規則性を見つけられていない。' +
      ' <phoneme> でアクセント核を指定しても改善しない駅があり、読み指定では届かない層 (文中の位置による抑揚) が関わっている可能性がある。',
    nextAction:
      '揺れる駅名 / 揺れない駅名を一覧化し、拍数・語尾・文中の位置で分類して規則性を探す。単独読みと文中読みの比較も録る。',
    tags: ['station-name', 'accent'],
  },
  {
    id: 'line-name-intonation',
    title: '線区名イントネーション',
    status: 'unsolved',
    verifiedAt: '2026-09-15',
    problem:
      '線区名 (例: 「今里筋線」) が地名 + 「線」として不自然に区切られ、路線名としてひとまとまりに聞こえない。',
    cause:
      '**特定できていない。** 「線」の直前で区切りが入るように聞こえるが、<break> を入れていない箇所で起きるため、' +
      '合成側が語境界をどう判断しているかは分からない。<sub> でカナを与えても区切りが残る線区がある。',
    nextAction:
      '線区名ごとに <phoneme> / <sub> / 間の挿入を試し、改善する線区と改善しない線区を分けて記録する。改善しない線区に共通点があるかを見る。',
    tags: ['line-name', 'accent'],
  },
  {
    id: 'tokyo-pitch',
    title: '東京の音程問題',
    status: 'unsolved',
    verifiedAt: '2026-09-24',
    problem:
      '「東京」の音の高さが他の地名と揃わず、文中で浮いて聞こえる (地名として不自然に感じる) という指摘がある。',
    cause:
      '**原因不明。** 音の高さを直接変える <prosody pitch> は Neural エンジン (話者 Takumi) で **合成が失敗する** ため使えない' +
      ' (2026-09-24 実機確認: pitch="+10%" / pitch="-5%" / rate との併用のいずれも失敗し、pitch を外すと成功)。' +
      '<phoneme> のアクセント核指定では音の高さそのものが変わらず、他の地名と同じ高さに揃えられていない。',
    nextAction:
      '「東京」と他の地名を同じ文型で合成して音高を実測し (scripts/audio-analyze.py)、どれだけ差があるかを数値で押さえる。主観の共有だけでは判断できない。' +
      'pitch 以外の手段 (<phoneme> のアクセント核の位置 / 前後の <break> / rate・volume) で浮きが変わるかを試す。',
    tags: ['station-name', 'accent'],
  },
];
